/**
 * IndexedDB draft store and crash recovery manager for Paper2Ink.
 * Spec reference: .hermes/plans/2026-09-16_195645-paper2ink-saas-spec.md (§10.2, SAAS-217).
 *
 * Guarantees:
 * - Crash recovery: drafts persisted with timestamp, revision, branch, dirty status.
 * - Strict user namespace isolation (p2i_draft_{userId}_{docId}).
 * - QuotaExceeded handling: visible alert and immediate fallback JSON export (no silent data loss).
 * - Safe user logout namespace purging with unsynced change warnings.
 */

import { validateCIMDocument, type CIMDocument } from '../editor/model.ts';
import { exportToJSON } from '../export/jsonExport.ts';
import type {
  DraftRecord,
  StorageSaveResult,
  ClearNamespaceResult,
  IStorageAdapter,
} from './types.ts';

export class MemoryStorageAdapter implements IStorageAdapter {
  private readonly store: Map<string, string> = new Map();
  private quotaLimitBytes: number | null = null;

  setQuotaLimitBytes(bytes: number | null): void {
    this.quotaLimitBytes = bytes;
  }

  private currentSize(): number {
    let bytes = 0;
    for (const [k, v] of this.store.entries()) {
      bytes += k.length + v.length;
    }
    return bytes;
  }

  async get(key: string): Promise<string | null> {
    return this.store.get(key) ?? null;
  }

  async set(key: string, value: string): Promise<void> {
    const existing = this.store.get(key);
    const delta = (key.length + value.length) - (existing ? key.length + existing.length : 0);

    if (this.quotaLimitBytes !== null && this.currentSize() + delta > this.quotaLimitBytes) {
      const err = new Error('QuotaExceededError: storage capacity exceeded');
      err.name = 'QuotaExceededError';
      throw err;
    }
    this.store.set(key, value);
  }

  async delete(key: string): Promise<void> {
    this.store.delete(key);
  }

  async listKeys(prefix: string): Promise<string[]> {
    const results: string[] = [];
    for (const k of this.store.keys()) {
      if (k.startsWith(prefix)) {
        results.push(k);
      }
    }
    return results;
  }
}

export class IndexedDBStorageAdapter implements IStorageAdapter {
  private readonly dbName = 'paper2ink_offline_db';
  private readonly storeName = 'drafts';

  private async getDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available in current environment'));
        return;
      }
      const request = indexedDB.open(this.dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async get(key: string): Promise<string | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const req = store.get(key);
      req.onsuccess = () => resolve((req.result as string) ?? null);
      req.onerror = () => reject(req.error);
    });
  }

  async set(key: string, value: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const req = store.put(value, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async delete(key: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async listKeys(prefix: string): Promise<string[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        const keys = (req.result as string[]).filter((k) => typeof k === 'string' && k.startsWith(prefix));
        resolve(keys);
      };
      req.onerror = () => reject(req.error);
    });
  }
}

export class DraftStore {
  private userId: string;
  private readonly adapter: IStorageAdapter;

  constructor(userId: string, adapter?: IStorageAdapter) {
    if (!userId || typeof userId !== 'string') {
      throw new Error('DraftStore requires a valid, non-empty userId for namespace isolation');
    }
    this.userId = userId;
    this.adapter = adapter ?? (typeof indexedDB !== 'undefined' ? new IndexedDBStorageAdapter() : new MemoryStorageAdapter());
  }

  setUserId(newUserId: string): void {
    if (!newUserId || typeof newUserId !== 'string') {
      throw new Error('Valid non-empty newUserId required');
    }
    this.userId = newUserId;
  }

  get activeUserId(): string {
    return this.userId;
  }

  private keyFor(docId: string): string {
    return `p2i_draft_${this.userId}_${docId}`;
  }

  private userPrefix(): string {
    return `p2i_draft_${this.userId}_`;
  }

  async saveDraft(
    doc: CIMDocument,
    options?: { branchId?: string; isDirty?: boolean }
  ): Promise<StorageSaveResult> {
    validateCIMDocument(doc);

    const record: DraftRecord = {
      documentId: doc.document_id,
      userId: this.userId,
      revisionId: doc.revision_id,
      branchId: options?.branchId ?? 'main',
      document: structuredClone(doc),
      updatedAt: new Date().toISOString(),
      isDirty: options?.isDirty ?? true,
    };

    const key = this.keyFor(doc.document_id);
    const serialized = JSON.stringify(record);

    try {
      await this.adapter.set(key, serialized);
      return {
        success: true,
        quotaExceeded: false,
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      const isQuota =
        (err instanceof Error && err.name === 'QuotaExceededError') ||
        errorMsg.toLowerCase().includes('quota');

      if (isQuota) {
        // Provide immediate JSON download backup to prevent silent data loss (§10.2)
        const fallbackJson = exportToJSON(doc, { includeSource: true });
        return {
          success: false,
          quotaExceeded: true,
          error: 'Local storage quota exceeded. Please download your document backup.',
          fallbackJson,
        };
      }

      return {
        success: false,
        quotaExceeded: false,
        error: errorMsg,
      };
    }
  }

  async getDraft(documentId: string): Promise<DraftRecord | null> {
    const key = this.keyFor(documentId);
    const raw = await this.adapter.get(key);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as DraftRecord;
      if (parsed.userId !== this.userId) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  async listDrafts(): Promise<DraftRecord[]> {
    const keys = await this.adapter.listKeys(this.userPrefix());
    const drafts: DraftRecord[] = [];

    for (const k of keys) {
      const raw = await this.adapter.get(k);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as DraftRecord;
        if (parsed.userId === this.userId) {
          drafts.push(parsed);
        }
      } catch {
        // Skip malformed records
      }
    }

    return drafts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async deleteDraft(documentId: string): Promise<void> {
    const key = this.keyFor(documentId);
    await this.adapter.delete(key);
  }

  async clearUserNamespace(): Promise<ClearNamespaceResult> {
    const drafts = await this.listDrafts();
    const hasUnsynced = drafts.some((d) => d.isDirty);

    const keys = await this.adapter.listKeys(this.userPrefix());
    for (const k of keys) {
      await this.adapter.delete(k);
    }

    return {
      clearedCount: keys.length,
      unsyncedWarning: hasUnsynced,
    };
  }
}
